import { useState, useCallback, useRef, useEffect } from 'react';
import api from '../../api/client';

function htmlToText(html) {
  return (html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .trim();
}

function textToHtml(text) {
  const t = (text || '').trim();
  if (!t) return '';
  return t
    .split(/\n\n+/)
    .map((p) => `<p>${p.replace(/\n/g, '<br />')}</p>`)
    .join('\n');
}

export default function ProductEdit({ product, onClose, onUpdated }) {
  const [name, setName] = useState(product.name || '');
  const [regularPrice, setRegularPrice] = useState(
    product.regular_price != null ? String(product.regular_price) : ''
  );
  const [salePrice, setSalePrice] = useState(
    product.sale_price != null ? String(product.sale_price) : ''
  );
  const [shortDesc, setShortDesc] = useState(htmlToText(product.short_description));
  const [desc, setDesc] = useState(htmlToText(product.description));
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(product.image_url || null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const fileInputRef = useRef(null);
  const nameInputRef = useRef(null);

  useEffect(() => {
    if (nameInputRef.current) nameInputRef.current.focus();
  }, []);

  const handleImageChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setError('');
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const updates = {};

      if (name.trim() !== product.name) updates.name = name.trim();
      if (regularPrice !== String(product.regular_price)) updates.regular_price = regularPrice;
      if (salePrice !== String(product.sale_price ?? '')) updates.sale_price = salePrice;

      const currentShortDesc = htmlToText(product.short_description);
      const currentDesc = htmlToText(product.description);
      if (shortDesc !== currentShortDesc) updates.short_description = textToHtml(shortDesc);
      if (desc !== currentDesc) updates.description = textToHtml(desc);

      if (imageFile) {
        const media = await api.uploadMedia(imageFile);
        updates.image_id = media.id;
      }

      if (Object.keys(updates).length === 0) {
        onClose();
        return;
      }

      const updated = await api.updateProduct(product.id, updates);
      if (onUpdated) onUpdated(updated);
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to save changes.');
    } finally {
      setSubmitting(false);
    }
  }, [product, name, regularPrice, salePrice, shortDesc, desc, imageFile, onClose, onUpdated]);

  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) onClose();
  }

  const inputCls =
    'block w-full rounded-md border border-pos-border bg-white px-3 py-2 text-sm ' +
    'text-pos-foreground outline-none transition-shadow ' +
    'focus:border-pos-primary focus:ring-2 focus:ring-pos-primary/20';

  const labelCls = 'block text-xs font-medium text-pos-muted-fg mb-1';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label="Edit Product"
    >
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl bg-white shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 flex items-center justify-between border-b border-pos-border bg-white px-5 py-4">
          <h2 className="text-lg font-semibold text-pos-foreground">Edit Product</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex-shrink-0 rounded-lg p-1.5 text-pos-muted-fg transition-colors hover:bg-pos-muted hover:text-pos-foreground focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
            aria-label="Close"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-5 mt-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
            <svg className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className="space-y-5 px-5 py-5">

            {/* Image */}
            <div>
              <p className={labelCls}>Product Image</p>
              <div className="flex items-center gap-4">
                <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-lg border border-pos-border bg-gray-100">
                  {imagePreview ? (
                    <img src={imagePreview} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-gray-300">
                      <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1">
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                    </div>
                  )}
                </div>
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="sr-only"
                    onChange={handleImageChange}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="rounded-lg border border-pos-border px-3 py-1.5 text-sm font-medium text-pos-foreground transition-colors hover:bg-pos-muted focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
                  >
                    {imageFile ? 'Change again' : 'Change image'}
                  </button>
                  {imageFile && (
                    <p className="mt-1 text-xs text-pos-muted-fg truncate max-w-[180px]">{imageFile.name}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Name */}
            <div>
              <label className={labelCls} htmlFor="pe-name">Name</label>
              <input
                ref={nameInputRef}
                id="pe-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={submitting}
                className={inputCls}
                required
              />
            </div>

            {/* Price */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls} htmlFor="pe-price">Regular Price</label>
                <input
                  id="pe-price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={regularPrice}
                  onChange={(e) => setRegularPrice(e.target.value)}
                  disabled={submitting}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls} htmlFor="pe-sale">Sale Price</label>
                <input
                  id="pe-sale"
                  type="number"
                  min="0"
                  step="0.01"
                  value={salePrice}
                  onChange={(e) => setSalePrice(e.target.value)}
                  disabled={submitting}
                  className={inputCls}
                  placeholder="Leave blank for no sale"
                />
              </div>
            </div>

            {/* Short description */}
            <div>
              <label className={labelCls} htmlFor="pe-short-desc">Short Description</label>
              <textarea
                id="pe-short-desc"
                rows={2}
                value={shortDesc}
                onChange={(e) => setShortDesc(e.target.value)}
                disabled={submitting}
                className={inputCls + ' resize-y'}
                placeholder="Brief summary shown on product listings"
              />
            </div>

            {/* Description */}
            <div>
              <label className={labelCls} htmlFor="pe-desc">Description</label>
              <textarea
                id="pe-desc"
                rows={5}
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                disabled={submitting}
                className={inputCls + ' resize-y'}
                placeholder="Full product description"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 flex items-center justify-end gap-3 border-t border-pos-border bg-white px-5 py-4">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-lg border border-pos-border px-4 py-2 text-sm font-medium text-pos-foreground transition-colors hover:bg-pos-muted focus:outline-none focus:ring-2 focus:ring-pos-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !name.trim()}
              className="flex items-center gap-2 rounded-lg bg-pos-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-pos-primary-dark focus:outline-none focus:ring-2 focus:ring-pos-primary focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting && (
                <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              )}
              {submitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
